import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  listIpEntries,
  addIpEntry,
  removeIpEntry,
  syncWaf,
  type IpEntry,
  type AddIpEntryData,
} from '../../api/ip-allowlist';
import { DataTable, type Column } from '../common/DataTable';
import { Modal } from '../common/Modal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { CidrForm } from './CidrForm';

export function IpAllowlistView() {
  const { getJwtToken } = useAuth();

  const [entries, setEntries] = useState<IpEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IpEntry | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listIpEntries(token);
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load IP entries');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAdd = async (data: AddIpEntryData) => {
    const token = await getJwtToken();
    if (!token) return;
    await addIpEntry(data, token);
    setShowAddModal(false);
    await fetchEntries();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await removeIpEntry(deleteTarget.scope, deleteTarget.cidr, token);
      setDeleteTarget(null);
      await fetchEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove entry');
      setDeleteTarget(null);
    }
  };

  const handleSyncWaf = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const result = await syncWaf(token);
      setSuccess(result.message || 'WAF sync completed successfully.');
      setShowSyncConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync WAF');
      setShowSyncConfirm(false);
    } finally {
      setSyncing(false);
    }
  };

  const columns: Column<IpEntry>[] = [
    { key: 'cidr', header: 'CIDR', render: (e) => <code>{e.cidr}</code> },
    { key: 'scope', header: 'Scope' },
    { key: 'label', header: 'Label' },
    { key: 'enforce', header: 'Enforce' },
    {
      key: 'createdAt',
      header: 'Created',
      render: (e) => new Date(e.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      render: (e) => (
        <button
          className="settings-card__delete"
          onClick={(ev) => { ev.stopPropagation(); setDeleteTarget(e); }}
          type="button"
          title="Remove"
        >
          x
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">IP Allow-list</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add CIDR
          </button>
          <button
            className="btn btn--green"
            onClick={() => setShowSyncConfirm(true)}
            disabled={syncing}
            type="button"
          >
            {syncing ? 'Syncing...' : 'Sync WAF'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchEntries}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && entries.length === 0 ? (
        <div className="view__empty">Loading IP entries...</div>
      ) : (
        <DataTable<IpEntry>
          columns={columns}
          data={entries}
          keyExtractor={(e) => `${e.scope}:${e.cidr}`}
          emptyMessage="No IP entries found. Add a CIDR to get started."
        />
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add CIDR Entry"
      >
        <CidrForm
          onSubmit={handleAdd}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      <ConfirmDialog
        isOpen={showSyncConfirm}
        title="Sync WAF"
        message="This will push all IP allowlist entries to the WAF IP set. Existing WAF rules will be updated. Continue?"
        confirmLabel="Sync WAF"
        onConfirm={handleSyncWaf}
        onCancel={() => setShowSyncConfirm(false)}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove IP Entry"
        message={`Remove CIDR "${deleteTarget?.cidr}" (scope: ${deleteTarget?.scope})?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
